import { SCOPE } from '@store/noc/threshold';
import { createSelector } from 'reselect';
import { get } from 'lodash';
import {
  modes,
  AI_ALERT,
  getAudienceMetricThresholdMap,
  getAllBaseLineMetrics,
  getAllPercentageMetrics,
} from '@components/noc-metric-panel/utils/constants';
import { getBaseLineData } from '@components/noc-metric-panel/utils/getBaseLineData';
import {
  DATASET,
  POPULATION_THRESHOLD_BASELINE,
  INTERNAL_UNKNOWN,
  CURRENT_PRODUCT_MODE,
} from '@utils/constants';
import { selector as commonSelector } from '@store/common';
import { getMetricMetaByKey, getMetricUnitByKey, NOC_METRICS } from '@utils/metrics';
import { currentMetricsMetaSelector } from '@store/common/selectors';
import { combineWithMetricDeDuplication } from '@utils/metrics/metricHelpers';
import featureNames from '@utils/featureToggle/constants';
import {
  nocModeSelector,
  trendsViewSelector,
  unsavedThresholdSelector,
  allThresholdCellSelectionSelector,
} from '../base/selectors';
import {
  dataSelector,
  trendsTableDataSortSelector,
  dimensionSelector,
  metricSelector as trendsMetricSelector,
  trendsThresholdPayloadSelector,
} from '../trendsData/selectors';
import {
  filtersTableDataSortSelector,
  metricSelector as filterMetricSelector,
} from '../filtersData/selectors';

const THRESHOLD_MODES = {
  filter: 'filter',
  dimension: 'dimension',
};

const selector = (state) => get(state, SCOPE);
export default selector;

const trendsBody = {
  queries: [
    {
      dataset: DATASET,
      hasAIAlerts: true,
      metrics: [],
      metricsUnit: {},
      filter: [],
      groupBy: [],
      dimension_attributes: [],
      type: THRESHOLD_MODES.dimension,
    },
  ],
};

const filtersBody = {
  queries: [
    {
      dataset: DATASET,
      hasAIAlerts: true,
      metrics: [],
      metricsUnit: {},
      filter: [],
      type: THRESHOLD_MODES.filter,
    },
  ],
};

export const initThresholdSelector: any = createSelector(
  selector,
  nocModeSelector,
  (thresholdStore, mode) => {
    const dataPath = mode === modes.TRENDS ? 'trendsData.response[0]' : 'filtersData.response[0]';

    return {
      data: get(thresholdStore, dataPath, {}),
      isFetching: thresholdStore.isFetching,
    };
  },
);

export const dimensionNameValueMapSelector = createSelector(dataSelector, (dimensionStore) => {
  const results = get(dimensionStore, 'data[0].results', []);
  const mapObj = {};
  results.forEach((result) => {
    const name = result.name.join('');
    const value = result.value.join('');
    mapObj[value] = name;
  });
  return mapObj;
});

export const baseLineDataSelector = createSelector(initThresholdSelector, (thresholdStore) => {
  const baseLine = get(thresholdStore, 'data.threshold_baseline', {});
  const baseLineData = {
    id: '1',
    BaseLine: POPULATION_THRESHOLD_BASELINE,
  };

  const audienceMetricMap = getAudienceMetricThresholdMap();

  Object.keys(audienceMetricMap).forEach((metricName) => {
    const data = audienceMetricMap[metricName];
    baseLineData[metricName] = {
      metric: {
        key: metricName,
        metrics: data?.percentageMetrics,
      },
      data: getBaseLineData(baseLine, data?.percentageMetrics ?? []),
    };
  });
  return [baseLineData];
});

export const contentTableDataSelector = createSelector(
  initThresholdSelector,
  nocModeSelector,
  dimensionNameValueMapSelector,
  currentMetricsMetaSelector,
  (thresholdStore, mode, dimensionStore, supportMetrics) => {
    const data = get(thresholdStore, 'data.thresholds', []);
    const mapNames = (value) => dimensionStore[value] || value;

    return data.map((thresholdDataItem, idx) => {
      const content = {
        id: idx.toString(),
        RowKeys:
          mode === modes.TRENDS
            ? mapNames(thresholdDataItem.dimension_attribute)
            : thresholdDataItem.filter.name,
      };
      Object.keys(thresholdDataItem)
        .filter((key) => !['filter', 'dimension_attribute'].includes(key))
        .forEach((metricName) => {
          const metric = supportMetrics[metricName];
          if (metric) {
            content[metricName] = {
              metric: { ...metric, key: metricName },
              data: {
                custom: thresholdDataItem[metricName].custom || '',
                conviva: thresholdDataItem[metricName].conviva || '',
                isCustomSelected: thresholdDataItem[metricName].isCustomSelected || false,
              },
              identifier:
                mode === modes.TRENDS
                  ? thresholdDataItem.dimension_attribute
                  : thresholdDataItem.filter.name,
              unit: metric.unit,
            };
          }
        });

      return content;
    });
  },
);

export const thresholdDimensionSelector = createSelector(
  trendsTableDataSortSelector,
  dimensionSelector,
  (trendsDataStore, trendsDimensionStore) => {
    const { data } = trendsDataStore;
    const dimennsionAttributes = data.map((resultItem) => resultItem.value.join(''));
    return {
      dimension: trendsDimensionStore,
      dimension_attributes: dimennsionAttributes,
    };
  },
);

export const thresholdFilterSelector = createSelector(
  filtersTableDataSortSelector,
  (filterDataStore) => {
    const results = filterDataStore.data;
    return results.map((resultItem) => resultItem.filter);
  },
);

export const thresholdFilterPayloadSelector = createSelector(
  filterMetricSelector,
  thresholdFilterSelector,
  commonSelector,
  (filterMetrics, filtersDataSelector, commonStore) => {
    const metrics = combineWithMetricDeDuplication(filterMetrics, [
      ...getAllBaseLineMetrics(),
      ...getAllPercentageMetrics(),
    ]);
    const metricsUnit = Object.fromEntries(
      metrics.map((metric) => {
        const unit = getMetricUnitByKey({ metricKey: metric });
        return [metric, unit];
      }),
    );
    filtersBody.queries[0].dataset = DATASET;
    filtersBody.queries[0].hasAIAlerts = commonStore.features[featureNames[AI_ALERT]];
    filtersBody.queries[0].metrics = metrics;
    filtersBody.queries[0].metricsUnit = metricsUnit;
    filtersBody.queries[0].filter = filtersDataSelector;
    return filtersBody;
  },
);

export const thresholdTrendsPayloadSelector = createSelector(
  trendsThresholdPayloadSelector,
  trendsMetricSelector,
  thresholdDimensionSelector,
  commonSelector,
  (trendsPayloadStore, trendsMetrics, thresholdDimensionStore, commonStore) => {
    const metrics = combineWithMetricDeDuplication(trendsMetrics, [
      ...getAllBaseLineMetrics(),
      ...getAllPercentageMetrics(),
    ]);
    const metricsUnit = Object.fromEntries(
      metrics.map((metric) => {
        const unit = getMetricUnitByKey({ metricKey: metric });
        return [metric, unit];
      }),
    );
    const dimension = get(thresholdDimensionStore, 'dimension', '');
    trendsBody.queries[0].dataset = DATASET;
    trendsBody.queries[0].hasAIAlerts = commonStore.features[featureNames[AI_ALERT]];
    trendsBody.queries[0].metrics = metrics;
    trendsBody.queries[0].metricsUnit = metricsUnit;
    trendsBody.queries[0].filter = get(trendsPayloadStore, 'queries[0].filter', {});
    // trendsBody.queries[0].filter = get(commonStore, 'appliedFilters[0]', {});
    trendsBody.queries[0].groupBy = dimension;
    trendsBody.queries[0].dimension_attributes = get(
      thresholdDimensionStore,
      'dimension_attributes',
      [],
    ).filter((dimensionValue) => dimensionValue !== INTERNAL_UNKNOWN);
    return trendsBody;
  },
);

export const thresholdPayloadSelector = createSelector(
  nocModeSelector,
  thresholdFilterPayloadSelector,
  thresholdTrendsPayloadSelector,
  (mode, thresholdFilterStore, thresholdTrendsStore) =>
    mode === modes.FILTERS ? thresholdFilterStore : thresholdTrendsStore,
);

export const thresholdContentColumnSelector = createSelector(
  nocModeSelector,
  trendsMetricSelector,
  filterMetricSelector,
  currentMetricsMetaSelector,
  (mode, trendsMetrics, filterMetrics, supportedMetrics) => {
    const metrics = mode === modes.TRENDS ? trendsMetrics : filterMetrics;
    const columns: Array<{ key: string; title: string }> = [];
    metrics.forEach((key) => {
      const metric = supportedMetrics[key];
      if (metric) {
        columns.push({
          key,
          title: metric.displayName,
        });
      }
    });
    return columns;
  },
);

export const trendsSavePayloadSelector = createSelector(
  trendsViewSelector,
  thresholdDimensionSelector,
  (trendsStore, thresholdDimensionStore) => {
    const appliedFilter = get(trendsStore, 'appliedFilter[0]', {});
    const dimension = get(thresholdDimensionStore, 'dimension', '');
    return {
      queries: [
        {
          dataset: DATASET,
          filter: appliedFilter,
          dimension,
          type: THRESHOLD_MODES.dimension,
        },
      ],
    };
  },
);

export const filtersSavePayloadSelector = createSelector(nocModeSelector, (mode) => ({
  queries: [
    {
      dataset: DATASET,
      type: THRESHOLD_MODES.filter,
    },
  ],
}));

export const loadingSelector = createSelector(
  selector,
  nocModeSelector,
  (commonThresholdSelector, mode) => {
    const isFetching =
      mode === modes.TRENDS
        ? commonThresholdSelector.trendsData.isFetching
        : commonThresholdSelector.filtersData.isFetching;

    const errorCode =
      mode === modes.TRENDS
        ? commonThresholdSelector.trendsData.errorCode
        : commonThresholdSelector.filtersData.errorCode;

    const errorMsg =
      mode === modes.TRENDS
        ? commonThresholdSelector.trendsData.errorMsg
        : commonThresholdSelector.filtersData.errorMsg;

    return {
      loading: isFetching,
      errorCode,
      errorMsg,
    };
  },
);

export const thresholdCellConfigSelector = createSelector(
  unsavedThresholdSelector,
  allThresholdCellSelectionSelector,
  commonSelector,
  (unsavedThresholdChanges, allThresholdCellSelection, commonStore) => ({
    unsavedThresholdChanges,
    allThresholdCellSelection,
    aiThresholdEnabled:
      commonStore.features[featureNames[AI_ALERT]] &&
      commonStore.features[featureNames.REALTIME_AI_THRESHOLD],
    thresholdDisabled: commonStore.features[featureNames.DISABLE_THRESHOLD],
  }),
);

// threshold feature flag selector
export const isThresholdDisabledSelector = createSelector(commonSelector, (commonStore) =>
  get(commonStore, ['features', featureNames.DISABLE_THRESHOLD], null),
);
